/**
 * LockKey (rules v2) view helpers, free of Phaser so they are unit-tested in node: which tiles
 * show a padlock, which pieces wear the key badge, and why a tap is refused. Core decides every
 * rule; these only read its compiled level and state.
 *
 * The key is an ITEM TYPE, not a tile: a shuffle can move the key's label between open tiles, so
 * "is this piece a key" is always `r.item[i] === L.lockKey`, read again after every change.
 */
import { PIECE_BOARD, canMagnet, isLocked, isRevealed, lineShown, type Round, type RoundEvent } from '@mp/core';

/** Does the level have locks at all (the views skip every lock pass otherwise)? */
export const hasLocks = (r: Round): boolean => r.L.lockedTiles.length > 0;

/** Tiles that show a padlock now: every locked tile while the locks are shut (none once open). */
export function padlockTiles(r: Round): number[] {
  if (!r.lockShut) return [];
  return r.L.lockedTiles.filter((i) => r.piece[i] === PIECE_BOARD);
}

/**
 * Does piece i wear the key badge? While the locks are shut, every piece still on the board
 * (tiles, conveyor entries, dispenser items) whose item is the key, except a wrapped tile that
 * has not shown its item yet (the badge would give the wrap away).
 */
export function isKeyPiece(r: Round, i: number): boolean {
  return r.lockShut && r.L.lockKey >= 0 && r.piece[i] === PIECE_BOARD && r.item[i] === r.L.lockKey && isRevealed(r, i);
}

/** Every piece that wears the key badge now (the views still decide which ones are drawn). */
export function keyPieces(r: Round): number[] {
  const out: number[] = [];
  if (!r.lockShut || r.L.lockKey < 0) return out;
  for (let i = 0; i < r.L.n; i++) if (isKeyPiece(r, i)) out.push(i);
  return out;
}

/**
 * Is the key one of the ticket's items (its ticket line wears the badge too)? Line indices, or [].
 * hiddenLine: a face-down line never wears it (the badge would name its item).
 */
export function keyLines(r: Round): number[] {
  const out: number[] = [];
  if (!r.lockShut || r.L.lockKey < 0) return out;
  for (let l = 0; l < r.L.nLines; l++) if (r.L.lineItem[l] === r.L.lockKey && lineShown(r, l)) out.push(l);
  return out;
}

/**
 * Why core refuses a tap on piece i, mirroring its `illegal` reasons without applying the action
 * (the scene never sends a refused tap): 'locked' only for an uncovered padlocked tile (a covered
 * locked tile is 'covered' first, as in core).
 */
export function tapRefusal(r: Round, i: number): 'locked' | 'covered' {
  return r.piece[i] === PIECE_BOARD && r.cover[i] === 0 && isLocked(r, i) ? 'locked' : 'covered';
}

/** The `unlocked` event of a batch, if any (at most one per round). */
export function unlockOf(events: readonly RoundEvent[]): Extract<RoundEvent, { type: 'unlocked' }> | null {
  for (const e of events) if (e.type === 'unlocked') return e;
  return null;
}

/**
 * Is the magnet unavailable only because of the padlocks (it would have a triple to take with
 * the locks open)? The button reads disabled like any other time core says no; a tap on it then
 * rattles the padlocks as a hint. Reads a shallow copy with the locks open; never mutates `r`.
 */
export function magnetHeldByLocks(r: Round): boolean {
  return r.lockShut && !canMagnet(r) && canMagnet({ ...r, lockShut: false });
}
